// src/pages/public/Services.jsx
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import {
    TrendingUp,
    Zap,
    Shield,
    Clock,
    DollarSign,
    ArrowRight,
    Star,
    Check,
    Crown,
    Rocket
} from 'lucide-react';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import PackageCard from '../../components/PackageCard';
import InvestmentCalculator from '../../components/InvestmentCalculator';

const Services = () => {
    const [selectedCategory, setSelectedCategory] = useState('all')
    const [showCalculator, setShowCalculator] = useState(false)
    const location = useLocation()



const { data: services, isLoading, error,refetch } = useQuery({
    queryKey: ['public-services'],
    queryFn: async () => {
        try {
            const response = await api.get(endpoints.public.services)
            // Return the packages array directly
            console.log('Fetched services response:', response.data.packages) 
            return response.data.packages || []
        } catch (error) {
            console.error('Failed to fetch services:', error)
            return []
        }
    },
    staleTime: 5 * 60 * 1000,
})

// Debug: Check the actual data structure
console.log('Services data (packages array):', services)

const categories = [
    { 
        id: 'all', 
        name: 'All Packages', 
        icon: Rocket, 
        count: Array.isArray(services) ? services.length : 0 
    },
    { 
        id: 'Starter', 
        name: 'Starter', 
        icon: Star, 
        count: Array.isArray(services) ? services.filter(p => 
            String(p.level).toUpperCase() === 'STARTER'
        ).length : 0 
    },
    { 
        id: 'Level 2', 
        name: 'Advanced', 
        icon: TrendingUp, 
        count: Array.isArray(services) ? services.filter(p => 
            String(p.level).toUpperCase() === 'LEVEL 2'
        ).length : 0 
    },
    { 
        id: 'Level 3', 
        name: 'Professional', 
        icon: Crown, 
        count: Array.isArray(services) ? services.filter(p => 
            String(p.level).toUpperCase() === 'LEVEL 3'
        ).length : 0 
    },
    { 
        id: 'Level 4', 
        name: 'Premium', 
        icon: Crown, 
        count: Array.isArray(services) ? services.filter(p => 
            String(p.level).toUpperCase() === 'LEVEL 4'
        ).length : 0 
    },
    { 
        id: 'Level 5', 
        name: 'VIP', 
        icon: Crown, 
        count: Array.isArray(services) ? services.filter(p => 
            String(p.level).toUpperCase() === 'LEVEL 5'
        ).length : 0 
    }
]

const filteredPackages = Array.isArray(services) ? services.filter(pkg => {
    if (selectedCategory === 'all') return true
    return String(pkg.level).toUpperCase() === String(selectedCategory).toUpperCase()
}) : []

    const features = [
        {
            icon: Zap,
            title: "Instant Withdrawals",
            description: "Withdraw your earnings instantly with zero waiting period",
            color: "green"
        },
        {
            icon: Shield,
            title: "Maximum Security",
            description: "Bank-level security with insurance protection on all investments",
            color: "blue"
        },
        {
            icon: TrendingUp,
            title: "Highest ROI",
            description: "Industry-leading returns with daily compounding interest",
            color: "purple"
        },
        {
            icon: Clock,
            title: "24/7 Monitoring",
            description: "Real-time portfolio tracking and AI-powered optimization",
            color: "orange"
        }
    ]
useEffect(() => {
    refetch()
}, [location.pathname, refetch])

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20 pt-20">
            {/* Hero Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <div className="container mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-4xl mx-auto"
                    >
                        <h1 className="text-5xl lg:text-6xl font-bold mb-6">
                            Premium{" "}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                                Investment Packages
                            </span>
                        </h1>
                        <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                            Choose from our carefully designed investment packages with industry-leading returns,
                            instant withdrawals, and maximum security protection.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            {/* <button
                                onClick={() => setShowCalculator(true)}
                                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-3 transition-all transform hover:scale-105"
                            >
                                <DollarSign className="w-6 h-6" />
                                Calculate Your Returns
                            </button> */}
                            <Link
                                to="https://udash.yestimeearn.com/auth/register"
                                className="bg-yellow-400  hover:border-white text-white px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-3 transition-all backdrop-blur-sm hover:bg-white/10"
                            >
                                <TrendingUp className="w-6 h-6" />
                                Start Investing Now
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features Banner */}
            <section className="py-12 bg-white dark:bg-gray-800 shadow-lg">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="text-center group"
                            >
                                <div className={`bg-gradient-to-r from-${feature.color}-500 to-${feature.color}-600 w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                                    <feature.icon className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                                    {feature.title}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {feature.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Packages Section */}
            <section className="py-20">
                <div className="container mx-auto px-4">
                    {/* Category Filter */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-wrap justify-center gap-4 mb-12"
                    >
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`flex items-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all ${selectedCategory === category.id
                                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                            >
                                <category.icon className="w-5 h-5" />
                                {category.name}
                                <span className={`px-2 py-1 text-xs rounded-full ${selectedCategory === category.id
                                        ? 'bg-white/20 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                    }`}>
                                    {category.count}
                                </span>
                            </button>
                        ))}
                    </motion.div>

                    {/* Packages Grid */}
                    {isLoading ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[...Array(6)].map((_, index) => (
                                <div key={index} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg animate-pulse">
                                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <motion.div
                            key={selectedCategory}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                        >
                            {filteredPackages?.map((pkg, index) => (
                                <PackageCard key={pkg.id} pkg={pkg} index={index} />
                            ))}
                        </motion.div>
                    )}

                    {/* Empty State */}
                    {!isLoading && filteredPackages?.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center py-12"
                        >
                            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                <TrendingUp className="w-10 h-10 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                No packages found
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                Try selecting a different category or check back later for new packages.
                            </p>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* Investment Calculator Modal */}
            {showCalculator && (
                <InvestmentCalculator
                    packages={services?.services}
                    onClose={() => setShowCalculator(false)}
                />
            )}

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <div className="container mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="max-w-3xl mx-auto"
                    >
                        <h2 className="text-4xl font-bold mb-6">
                            Ready to Start Your Investment Journey?
                        </h2>
                        <p className="text-xl mb-8 text-blue-100">
                            Join thousands of investors who trust YesTimeEarn with their financial growth
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="https://udash.yestimeearn.com/auth/register"
                                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-2 transition-all transform hover:scale-105"
                            >
                                Get Started Now
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                            <Link
                                to="/contact"
                                className="border-2 border-white/50 hover:border-white text-white px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-2 transition-all backdrop-blur-sm"
                            >
                                Contact Support
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    )
}

export default Services